import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo, Brand, BrandText } from "@/components/Logo";
import { ArrowLeft, ShieldCheck, Database, Ban, MapPin, UserCheck, Mail, FileClock, Megaphone } from "lucide-react";

const NEVER = [
  { t: "No analytics pixels.", d: "We do not embed any third-party analytics scripts in the app." },
  { t: "No cross-site behavioral profiling by us.", d: "We do not build our own profile of what you do on websites that are not aiBLACK." },
  { t: "No selling, renting, or sharing for third-party use.", d: "We will never sell, rent, or otherwise share your personal data with a third party for their own purposes." },
  { t: "One first-party banner of honesty.", d: "aiBLACK does ship a small, first-party page-view beacon (called PolsiaAnalytics) that counts visits to the deployed site. It sets a randomly-generated visitor ID in your browser's local storage and fires a single pixel per page load from the deploy's own domain. No personally identifying information is attached to that beacon beyond the random visitor ID." },
];

const COLLECT = [
  { t: "Account information.", d: "The information you provide when creating your account — such as your email address, your password (or sign-in via a third-party provider like Google), and the display name you choose. We use this to authenticate you and to put your name on your profile." },
  { t: "Chat history.", d: "The messages you send to aiBLACK and the assistant's replies are saved against your account so you can revisit them later. Pro features (such as long-term memory) rely on this history too." },
  { t: "Other content you create.", d: "Anything you write, post, or save while signed in — community posts, saved prompts, billing receipts — is stored against your account so the relevant feature can show it back to you." },
];

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-display font-black text-2xl tracking-tight text-white flex items-center gap-2.5 mb-4">
        <span className="w-9 h-9 rounded-lg bg-[#FF5A00]/15 flex items-center justify-center shrink-0">
          <Icon size={18} className="text-[#FF5A00]" />
        </span>
        {title}
      </h2>
      <div className="text-[#c7ccd6] leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
  );
}

export default function Privacy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-[#2a3040]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="privacy-back-button" onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors">
            <ArrowLeft size={18} /> Back
          </button>
          <Logo size="text-xl" />
        </div>
      </header>

      <motion.main initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="max-w-3xl mx-auto px-5 py-12" data-testid="privacy-page">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
          <ShieldCheck size={14} /> Your privacy
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mb-6">
          <Logo size="text-4xl sm:text-5xl" /> Privacy Policy
        </h1>

        <div className="bg-[#15151d] border border-[#FF5A00]/30 rounded-2xl p-6 mb-10">
          <p className="text-white text-lg leading-relaxed font-medium">
            <Brand /> does not track you outside the app and does not share your personal data with any third party.
          </p>
          <p className="text-[#9ca3af] mt-3 text-[15px] leading-relaxed">
            That is the heart of it. The rest of this page spells out exactly what we collect,
            what we will never do with it, how ads work on the free tier, and how to reach us.
          </p>
        </div>

        <Section icon={Database} title="What we collect">
          <p><Brand /> only collects the information the app needs to function for you:</p>
          <ul className="space-y-3">
            {COLLECT.map((c, i) => (
              <li key={i} className="pl-4 border-l-2 border-[#2a3040]">
                <span className="text-white font-semibold">{c.t}</span>{" "}<BrandText>{c.d}</BrandText>
              </li>
            ))}
          </ul>
          <p className="text-[#9ca3af]">That's the list. Nothing else is collected in the background.</p>
        </Section>

        <Section icon={Ban} title="What we do not do">
          <p>We make these plain promises about what we will never do with your information:</p>
          <ul className="space-y-3">
            {NEVER.map((c, i) => (
              <li key={i} className="pl-4 border-l-2 border-[#FF5A00]/40">
                <span className="text-white font-semibold">{c.t}</span>{" "}<BrandText>{c.d}</BrandText>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Megaphone} title="Advertising & cookies">
          <p><Brand /> is free to use, and ads help keep it that way. On the free tier we show advertising through{" "}
            <span className="text-white font-semibold">Google AdSense</span>.</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><span className="text-white font-semibold">Google and its partners use cookies</span> to serve and measure ads, and may show ads based on your prior visits to this and other websites.</li>
            <li>We do <span className="text-white font-semibold">not</span> share your <Brand /> account details — your email, name, or chat history — with advertisers.</li>
            <li>You can control ad personalization or opt out anytime at{" "}
              <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-[#FF5A00] hover:underline">Google Ads Settings</a>, and read how Google uses advertising data at{" "}
              <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-[#FF5A00] hover:underline">Google's advertising policies</a>.</li>
            <li><span className="text-white font-semibold"><Brand /> Pro members see no ads at all</span> — upgrading gives you a completely ad-free experience.</li>
          </ul>
        </Section>

        <Section icon={MapPin} title="Where your data lives">
          <p>Your account information, chat history, and any content you create are stored against your account so that <Brand /> can:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>authenticate you when you sign in;</li>
            <li>show you your past conversations and saved content;</li>
            <li>deliver the features you signed up for — including Pro-only memory and personalization.</li>
          </ul>
          <p>We retain your data while your account is active. If you would like us to delete it, the "Your choices" section below tells you exactly who to email.</p>
        </Section>

        <Section icon={UserCheck} title="Your choices">
          <p>You are in control of your account and your data:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>You can sign in at any time and view your chat history and saved content inside the app.</li>
            <li>You can sign out at any time. Signing out clears the session on the device you're using; your stored data is unaffected until you ask us to delete it.</li>
            <li>You can email us at{" "}
              <a href="mailto:support@aiblack.co" className="text-[#FF5A00] hover:underline">support@aiblack.co</a>{" "}
              with any privacy question, or to request that we delete your account and the data attached to it.</li>
          </ul>
        </Section>

        <Section icon={Mail} title="Contact">
          <p>For anything privacy-related — questions, requests, or concerns — please email{" "}
            <a href="mailto:support@aiblack.co" className="text-[#FF5A00] hover:underline">support@aiblack.co</a>.
            We read every message.</p>
        </Section>

        <Section icon={FileClock} title="Changes to this policy">
          <p>If we ever update this Privacy Policy, we will publish the new version on this page.
            The "Last updated" line below tells you when this page was most recently changed.</p>
        </Section>

        <p className="text-sm text-[#6b7280] border-t border-[#2a3040] pt-6">Last updated: 2026-08-25</p>
      </motion.main>
    </div>
  );
}
